import type { VoidFunctionComponent } from 'react';

import { window } from '../../../globals';
import { IconButton, icons, TooltipButton } from '../../generic';
import Flex from '../../generic/Flex';
import List from '../../generic/List';
import type { Organization } from '../../../types';

import styles from './ChangeOrgCard.module.less';

type MinOrg = Pick<Organization, 'id' | 'title' | 'access_level'>;

type OrganizationSearchListProps = {
  organizations: MinOrg[];
  isLoading: boolean;
  onSelectOrganization: (org: MinOrg) => void;
  isChanging: boolean;
  isPinned: (orgId: number) => boolean;
  onTogglePin: (orgId: number) => void;
};

/**
 * List component for displaying organization search results with selection capability.
 */
export const OrganizationSearchList: VoidFunctionComponent<OrganizationSearchListProps> = ({
  organizations,
  isLoading,
  onSelectOrganization,
  isChanging,
  isPinned,
  onTogglePin,
}) => {
  return (
    <List className={styles.organizationSearchList}>
      <List.Header>
        <div className={styles.organizationSearchList__header}>
          <List.HeaderText>Organization</List.HeaderText>
          <List.HeaderText>ID</List.HeaderText>
          <List.HeaderText>Access Level</List.HeaderText>
          <div className={styles.lastColumnItem}><List.HeaderText>Actions</List.HeaderText></div>
        </div>
      </List.Header>
      <List.Body maxHeight="400px" hasOverflowAuto>
        {isLoading && (
          <>
            <List.RowLoading />
            <List.RowLoading />
            <List.RowLoading />
          </>
        )}
        {!isLoading && organizations.length === 0 && (
          <List.RowContainer>
            <List.Row>
              <div>No organizations found</div>
            </List.Row>
          </List.RowContainer>
        )}
        {!isLoading &&
          organizations.sort((a, b) => a.id - b.id).map((org) => {
            const isOrgPinned = isPinned(org.id);
            return (
              <List.RowContainer key={org.id}>
                <List.Row isInteractive={false}>
                  <div className={styles.organizationSearchList__row}>
                    <span>{org.title}</span>
                    <span>{org.id}</span>
                    <span>{org.access_level}</span>
                    <div className={styles.lastColumnItem}>
                      <Flex gap="8px">
                        <TooltipButton
                          id={`change-to-org-${org.id}`}
                          tooltip={org.id === window.user?.organization?.id ? 'You are already on this organization' : undefined}
                          onClick={() => onSelectOrganization(org)}
                          isDisabled={isChanging || org.id === window.user?.organization?.id}
                          type="primary"
                          size="small"
                      >
                          Change to org
                        </TooltipButton>
                        <IconButton
                          id={`pin-${org.id}`}
                          tooltip={isOrgPinned ? 'Unpin organization' : 'Pin organization'}
                          icon={isOrgPinned ? icons.thumbtackSolid : icons.thumbtackSlash}
                          onClick={() => onTogglePin(org.id)}
                          isDisabled={isChanging}
                        />
                      </Flex>
                    </div>
                  </div>
                </List.Row>
              </List.RowContainer>
            );
          })}
      </List.Body>
    </List>
  );
};
